import React, { useCallback, useEffect, useRef } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import type { PersonProfileData } from '../types';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { HeaderActions } from './components/HeaderActions';
import { ManagerSection } from './components/ManagerSection';
import { RecentWorkSection } from './components/RecentWorkSection';

/**
 * Props for {@link UserProfileCard} - the presentational profile card.
 */
export interface UserProfileCardProps {
	/** Profile data to render. When omitted, the card shows a loading skeleton. */
	profileData?: PersonProfileData;
	/** URLs to render as smart-link previews in the recent-work section. */
	recentWorkData?: string[];
	/** When `true`, renders the loading skeleton even if `profileData` is supplied. */
	isLoading?: boolean;
	/** Test id forwarded to the card root. */
	testId?: string;
	/** Site ID of the tenant the viewer is currently in. */
	cloudId: string;
}

const styles = cssMap({
	root: {
		width: '360px',
		minWidth: '340px',
		cursor: 'default',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface.raised'),
	},
});

/**
 * Presentational profile card displaying a user's core information and recent work.
 * Renders avatar, name, pronouns, job title, department, location, manager, and recent-work items.
 *
 * Shows a loading skeleton when `isLoading` is true or `profileData` is undefined.
 * Displays an error state when profile data is missing or `name` is not provided.
 *
 * Recent-work previews require a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope (already provided by every Atlassian product shell).
 */
export function UserProfileCard({
	profileData,
	recentWorkData,
	isLoading = false,
	testId = 'user-profile-card',
	cloudId,
}: UserProfileCardProps): React.JSX.Element {
	// Note: do not add a `<TeamsAppAnalyticsContext source="userProfileCard">`
	// wrapper here. When this component is rendered via `UserProfileCardWithTrigger`
	// the parent already provides that context, and nesting the same source twice
	// can cause unexpected merged payloads. The error boundary instead consumes the
	// surrounding analytics context (whether provided by `UserProfileCardWithTrigger`
	// or — for standalone usage — by a consumer-supplied analytics listener).
	return (
		<UserProfileCardErrorBoundary testId={testId}>
			<UserProfileCardContent
				profileData={profileData}
				recentWorkData={recentWorkData}
				isLoading={isLoading}
				testId={testId}
				cloudId={cloudId}
			/>
		</UserProfileCardErrorBoundary>
	);
}

function UserProfileCardErrorBoundary({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const handleError = useCallback(() => {
		fireEvent('ui.userProfileCard.rendered.errorBoundary', {});
	}, [fireEvent]);

	return (
		<ErrorBoundary onError={handleError} fallback={<CardError testId={testId} />}>
			{children}
		</ErrorBoundary>
	);
}

function UserProfileCardContent({
	profileData,
	recentWorkData,
	isLoading,
	testId,
	cloudId,
}: Required<Pick<UserProfileCardProps, 'isLoading' | 'testId' | 'cloudId'>> &
	Pick<UserProfileCardProps, 'profileData' | 'recentWorkData'>): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	const hasProfileData = !!profileData && !!profileData.name;
	const hasFiredTriggeredRequest = useRef(false);
	const hasFiredTerminalRequest = useRef(false);

	// Fire `triggered.request` exactly once for the lifetime of this card mount,
	// so consumers can compute end-to-end fetch latency against `succeeded` / `failed`.
	useEffect(() => {
		if (hasFiredTriggeredRequest.current) {
			return;
		}
		hasFiredTriggeredRequest.current = true;
		fireEvent('operational.userProfileCard.triggered.request', {});
	}, [fireEvent]);

	// Fire exactly one terminal request status event (succeeded OR failed) per
	// mount. This guards against `isLoading` toggling true → false → true …, which
	// would otherwise re-fire `succeeded.request` / `failed.request` and break the
	// 1:1 pairing with `triggered.request`.
	useEffect(() => {
		if (isLoading || hasFiredTerminalRequest.current) {
			return;
		}
		hasFiredTerminalRequest.current = true;
		if (hasProfileData) {
			fireEvent('operational.userProfileCard.succeeded.request', {});
		} else {
			fireEvent('operational.userProfileCard.failed.request', {});
		}
	}, [isLoading, hasProfileData, fireEvent]);

	if (isLoading) {
		return <RenderedSpinner testId={testId} />;
	}

	if (!profileData || !profileData.name) {
		return <RenderedError testId={testId} errorType="NotFound" />;
	}
	const { name, pronouns, avatarUrl, jobTitle, department, location, localTime, manager } =
		profileData;

	const subtitleParts = [jobTitle, department].filter(Boolean);
	const subtitle = subtitleParts.join(` \u2022 `);

	const locationLine =
		localTime && location ? `${location} \u2013 ${localTime}` : (location ?? undefined);

	const hasRecentWork = !!(recentWorkData && recentWorkData.length > 0);

	return (
		<RenderedContent testId={testId} ariaLabel={name}>
			<Stack space="space.200">
				<Stack space="space.100">
					<Inline spread="space-between" alignBlock="start">
						<Avatar src={avatarUrl ?? undefined} name={name} size="xlarge" />
						<HeaderActions profileData={profileData} cloudId={cloudId} />
					</Inline>

					<Stack space="space.075">
						<Inline space="space.100" alignBlock="end">
							<Heading as="h2" size="medium">
								{name}
							</Heading>
							{pronouns && (
								<Text size="small" color="color.text.subtlest">
									{pronouns}
								</Text>
							)}
						</Inline>
						{subtitle && (
							<Text maxLines={2} color="color.text.subtlest">
								{subtitle}
							</Text>
						)}
						{locationLine && (
							<Text maxLines={2} size="small" color="color.text.subtlest">
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>

				{manager && <ManagerSection manager={manager} />}

				{hasRecentWork && recentWorkData && (
					<RecentWorkSection recentWorkData={recentWorkData} testId={testId} />
				)}
			</Stack>
		</RenderedContent>
	);
}

/**
 * Wraps {@link CardLoading} and fires `ui.userProfileCard.rendered.spinner` exactly
 * once per mount of the spinner state.
 */
function RenderedSpinner({ testId }: { testId: string }): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	useEffect(() => {
		fireEvent('ui.userProfileCard.rendered.spinner', {});
	}, [fireEvent]);
	return <CardLoading testId={testId} />;
}

/**
 * Wraps {@link CardError} and fires `ui.userProfileCard.rendered.error` with an
 * `errorType` classification once per mount of the error state.
 */
function RenderedError({
	testId,
	errorType,
}: {
	testId: string;
	errorType: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	useEffect(() => {
		fireEvent('ui.userProfileCard.rendered.error', { errorType });
	}, [fireEvent, errorType]);
	return <CardError testId={testId} />;
}

/**
 * Renders the populated card body and fires `ui.userProfileCard.rendered.content`
 * exactly once per mount, so consumers can measure perceived render time.
 */
function RenderedContent({
	children,
	testId,
	ariaLabel,
}: {
	children: React.ReactNode;
	testId: string;
	ariaLabel: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	useEffect(() => {
		fireEvent('ui.userProfileCard.rendered.content', {});
	}, [fireEvent]);

	return (
		<Box xcss={styles.root} testId={testId} role="region" aria-label={ariaLabel}>
			{children}
		</Box>
	);
}
